/**
 * Admin Injury Replacement Panel — mass-replace an injured player across all
 * squads in a cricket league that contain them.
 *
 * Flow:
 *   Step 1 — Select league, injured player, replacement player → Preview Impact
 *   Step 2 — Preview table showing all affected squads with banking impact
 *   Step 3 — Confirm dialog (requires checkbox) → atomic Firestore batch write
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { writeBatch, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  squadService,
  playerPoolService,
  userService,
  leaderboardSnapshotService,
} from '../../services/firestore';
import { useAuth } from '../../contexts/AuthContext';
import {
  computeInjuryReplacementForSquad,
  type InjuryReplacementPreview,
} from '../../utils/injuryReplacement';
import type { League, LeagueSquad, PlayerPoolEntry, User } from '../../types/database';

const SQUADS_COLLECTION = 'squads';

type Props = {
  leagues: League[];
};

interface PreviewResult {
  squadId: string;
  updatePayload: Partial<LeagueSquad>;
  preview: InjuryReplacementPreview;
}

const InjuryReplacementPanel: React.FC<Props> = ({ leagues }) => {
  const { user } = useAuth();

  // Only cricket leagues
  const cricketLeagues = useMemo(
    () =>
      leagues
        .filter((l) => l && l.sport !== 'tennis')
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [leagues],
  );

  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [squads, setSquads] = useState<LeagueSquad[]>([]);
  const [playerPool, setPlayerPool] = useState<PlayerPoolEntry[]>([]);
  const [usersByUid, setUsersByUid] = useState<Record<string, User>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [injuredPlayerId, setInjuredPlayerId] = useState<string>('');
  const [replacementPlayerId, setReplacementPlayerId] = useState<string>('');

  const [previews, setPreviews] = useState<PreviewResult[] | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const selectedLeague = useMemo(
    () => cricketLeagues.find((l) => l.id === selectedLeagueId) ?? null,
    [cricketLeagues, selectedLeagueId],
  );

  // Load squads + pool + users when league changes
  useEffect(() => {
    let cancelled = false;
    if (!selectedLeague) {
      setSquads([]);
      setPlayerPool([]);
      setUsersByUid({});
      setInjuredPlayerId('');
      setReplacementPlayerId('');
      setPreviews(null);
      return;
    }
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const [squadList, pool] = await Promise.all([
          squadService.getByLeague(selectedLeague.id),
          selectedLeague.playerPoolId
            ? playerPoolService.getById(selectedLeague.playerPoolId)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setSquads(squadList);
        setPlayerPool(pool?.players ?? []);
        const uniqueUids = Array.from(new Set(squadList.map((s) => s.userId)));
        const users = await userService.getByIds(uniqueUids);
        if (cancelled) return;
        const map: Record<string, User> = {};
        users.forEach((u) => { map[u.uid] = u; });
        setUsersByUid(map);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load league data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [selectedLeague]);

  // Players currently in at least one squad — these are the candidates for "injured"
  const playersInAnySquad = useMemo(() => {
    const seen = new Map<string, { playerId: string; name: string; team: string }>();
    squads.forEach((squad) => {
      squad.players.forEach((p) => {
        if (!seen.has(p.playerId)) {
          seen.set(p.playerId, { playerId: p.playerId, name: p.playerName, team: p.team });
        }
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [squads]);

  // Players in pool that are NOT in any squad in this league — valid replacements
  const validReplacementPool = useMemo(() => {
    const allSquadPlayerIds = new Set(squads.flatMap((s) => s.players.map((p) => p.playerId)));
    return playerPool
      .filter((p) => !allSquadPlayerIds.has(p.playerId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [playerPool, squads]);

  const injuredPoolEntry = useMemo(
    () => playerPool.find((p) => p.playerId === injuredPlayerId) ?? null,
    [playerPool, injuredPlayerId],
  );
  const replacementPoolEntry = useMemo(
    () => validReplacementPool.find((p) => p.playerId === replacementPlayerId) ?? null,
    [validReplacementPool, replacementPlayerId],
  );

  const canPreview = Boolean(
    selectedLeague && injuredPlayerId && replacementPlayerId && !loading,
  );

  const handlePreview = () => {
    if (!user || !replacementPoolEntry) return;
    setError('');
    setSuccessMessage('');
    setPreviews(null);

    const results: PreviewResult[] = [];
    for (const squad of squads) {
      const result = computeInjuryReplacementForSquad(
        squad,
        injuredPlayerId,
        replacementPoolEntry,
        user.uid,
      );
      if (result === null) continue;
      // Enrich preview with real user display name
      result.preview.userName =
        usersByUid[squad.userId]?.displayName ||
        usersByUid[squad.userId]?.email ||
        squad.squadName;
      results.push({
        squadId: squad.id,
        updatePayload: result.updatedSquad,
        preview: result.preview,
      });
    }
    setPreviews(results);
  };

  const handleConfirm = async () => {
    if (!user || !selectedLeague || !previews || previews.length === 0) return;
    setCommitting(true);
    setError('');
    try {
      // Chunk into batches of 500 (Firestore limit)
      const BATCH_SIZE = 500;
      for (let i = 0; i < previews.length; i += BATCH_SIZE) {
        const chunk = previews.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const { squadId, updatePayload } of chunk) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          batch.update(doc(db, SQUADS_COLLECTION, squadId), updatePayload as any);
        }
        await batch.commit();
      }
      await leaderboardSnapshotService.create(selectedLeague.id);
      const injuredName =
        injuredPoolEntry?.name || playersInAnySquad.find((p) => p.playerId === injuredPlayerId)?.name || injuredPlayerId;
      setSuccessMessage(
        `Successfully replaced ${injuredName} across ${previews.length} squad${previews.length !== 1 ? 's' : ''}. Leaderboard snapshot created.`,
      );
      setConfirmOpen(false);
      setPreviews(null);
      setInjuredPlayerId('');
      setReplacementPlayerId('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Batch commit failed. No squads were changed.');
    } finally {
      setCommitting(false);
      setConfirmChecked(false);
    }
  };

  const affectedCount = previews?.length ?? 0;
  const totalSquads = squads.length;
  const unaffectedCount = previews !== null ? totalSquads - affectedCount : 0;

  const hasCaptainInAnySquad = previews?.some((r) => r.preview.inheritedRole === 'captain') ?? false;

  return (
    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Injury Replacement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mass-replace an officially injured player across all squads in a cricket league. Transfer
        counters are NOT incremented. The injured player&apos;s earned contribution is banked for
        each affected squad. All changes are applied atomically.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Step 1 — Selectors */}
      <Stack spacing={2} sx={{ mb: 2 }}>
        <FormControl fullWidth disabled={cricketLeagues.length === 0}>
          <InputLabel>Cricket League</InputLabel>
          <Select
            label="Cricket League"
            value={selectedLeagueId}
            onChange={(e) => {
              setSelectedLeagueId(e.target.value);
              setInjuredPlayerId('');
              setReplacementPlayerId('');
              setPreviews(null);
            }}
          >
            <MenuItem value="">— Select league —</MenuItem>
            {cricketLeagues.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl fullWidth disabled={!selectedLeague || loading}>
            <InputLabel>Injured Player</InputLabel>
            <Select
              label="Injured Player"
              value={injuredPlayerId}
              onChange={(e) => {
                setInjuredPlayerId(e.target.value);
                setReplacementPlayerId('');
                setPreviews(null);
              }}
            >
              <MenuItem value="">— Select injured player —</MenuItem>
              {playersInAnySquad.map((p) => (
                <MenuItem key={p.playerId} value={p.playerId}>
                  {p.name} ({p.team})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!injuredPlayerId || loading}>
            <InputLabel>Replacement Player</InputLabel>
            <Select
              label="Replacement Player"
              value={replacementPlayerId}
              onChange={(e) => {
                setReplacementPlayerId(e.target.value);
                setPreviews(null);
              }}
            >
              <MenuItem value="">— Select replacement —</MenuItem>
              {validReplacementPool.map((p) => (
                <MenuItem key={p.playerId} value={p.playerId}>
                  {p.name} ({p.team}) — {p.points} pts
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box>
          <Button
            variant="contained"
            disabled={!canPreview}
            onClick={handlePreview}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            Preview Impact
          </Button>
          {loading && (
            <CircularProgress size={20} sx={{ ml: 2, verticalAlign: 'middle' }} />
          )}
        </Box>
      </Stack>

      {/* Step 2 — Preview */}
      {previews !== null && (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
            <Chip
              label={`${affectedCount} squad${affectedCount !== 1 ? 's' : ''} affected`}
              color={affectedCount > 0 ? 'warning' : 'default'}
            />
            <Chip
              label={`${unaffectedCount} squad${unaffectedCount !== 1 ? 's' : ''} unaffected`}
              color="default"
              variant="outlined"
            />
          </Stack>

          {hasCaptainInAnySquad && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The injured player is captain in one or more squads. The replacement will inherit the
              captain role in those squads.
            </Alert>
          )}

          {affectedCount === 0 ? (
            <Alert severity="info">
              No squads contain this player. No changes will be made.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Squad</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Role Inherited</TableCell>
                      <TableCell align="right">Points Banked</TableCell>
                      <TableCell align="right">Pts Before</TableCell>
                      <TableCell align="right">Pts After</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previews.map((r) => (
                      <TableRow key={r.squadId}>
                        <TableCell>{r.preview.squad.squadName}</TableCell>
                        <TableCell>{r.preview.userName}</TableCell>
                        <TableCell>
                          {r.preview.inheritedRole ? (
                            <Chip
                              label={r.preview.inheritedRole}
                              size="small"
                              color={r.preview.inheritedRole === 'captain' ? 'error' : 'warning'}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              none
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {r.preview.bankedDelta >= 0 ? '+' : ''}
                          {r.preview.bankedDelta.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">{r.preview.pointsBefore.toFixed(2)}</TableCell>
                        <TableCell align="right">{r.preview.pointsAfter.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setConfirmChecked(false);
                    setConfirmOpen(true);
                  }}
                >
                  Confirm Replacement for {affectedCount} Squad{affectedCount !== 1 ? 's' : ''}
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Step 3 — Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => !committing && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Injury Replacement</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This will atomically update <strong>{affectedCount} squad{affectedCount !== 1 ? 's' : ''}</strong>.
            Each affected squad will have:
          </Typography>
          <Box component="ul" sx={{ mb: 2 }}>
            <li>The injured player swapped out for the replacement</li>
            <li>The injured player&apos;s earned contribution banked</li>
            <li>Transfer counters unchanged (official ruling)</li>
            <li>A <code>injury_replacement</code> history entry stamped with your admin UID</li>
          </Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This operation is irreversible in bulk. Per-squad reversal is available in the Transfer
            Management tab using each squad&apos;s saved pre-transfer snapshot.
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                disabled={committing}
              />
            }
            label="I understand this will update all affected squads atomically."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={committing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!confirmChecked || committing}
            onClick={handleConfirm}
          >
            {committing ? 'Committing…' : `Apply to ${affectedCount} Squad${affectedCount !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InjuryReplacementPanel;
